import React from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ToastProvider } from './hooks/useToast';
import store, { persistor } from './store/configStore';
import PrivateRoute from './components/utility/PrivateRoute';
import Login from './hoc/Login/Login';
import Dashboard from './hoc/Dashboard/Dashboard';
import Registration from './hoc/Registration/Registration';
import ForgotPassword from './hoc/ForgotPassword/ForgotPassword';
import ResetPassword from './hoc/ResetPassword/ResetPassword';
import Verify from './hoc/Verify/Verify';
import VerifyEmailOtp from './hoc/Verify/VerifyEmailOtp';
import AuthCallback from './hoc/AuthCallback/AuthCallback';

import DashboardBrand from './hoc/DashboardBrand/DashboardBrand';
import MyCampaigns from './components/main/MyCampaigns/MyCampaigns';
import CreateCampaign from "./hoc/CreateCampaign/CreateCampaign";
import Forbidden from './hoc/Forbidden/Forbidden';
import NotFound from './hoc/NotFound/NotFound';
import BrandBusinessDashboard from './hoc/BrandBusinessDashboard/BrandBusinessDashboard';
import InfluencerChannelDashboard from './hoc/InfluencerChannelDashboard/InfluencerChannelDashboard';
import { APPLE_CONFIG } from './store/constants';
import './App.css';
import CampaignsList from "./components/main/InfluencerCampaigns/CampaignsList";
import CampaignsDetails from "./components/main/InfluencerCampaigns/CampaignsDetails";
import UploadCampaignVideos from "./components/main/InfluencerCampaigns/UploadCampaignVideos";
import BrandCampaignList from './components/main/BrandCampaignList/BrandCampaignList';
import ApprovedList from './components/main/ApprovedList/ApprovedList';
import InfluencerChannelAuthOnly from "./hoc/InfluencerChannelDashboard/InfluencerChannelAuthOnly";
import BrandWalletAndTransactions from './hoc/BrandWalletAndTransactions/BrandWalletAndTransactions';
import BrandCampaignsDetails from './components/main/BrandCampaignsDetails/BrandCampaignsDetails';
import InfluencerWalletAndTransactions from './hoc/InfluencerWalletAndTransactions/InfluencerWalletAndTransactions';
import GlobalLoader from './components/common/GlobalLoader/GlobalLoader';
import MergePopup from './components/popups/MergePopup/MergePopup';
import UploadCampaignUGCVideos from './components/main/UploadCampaignUGCVideos/UploadCampaignUGCVideos';
import MyChannelsPage from './components/main/MyChannels/MyChannels';
import InfluencerDashboard from './components/main/InfluencerDashboard/InfluencerDashboard';
import BrandDashboardV2 from './components/main/BrandDashboardV2/BrandDashboardV2';
import YoutubeNotPublishedVideos from './hoc/YoutubeNotPublishedVideos/YoutubeNotPublishedVideos';
import ChooseAccessType from './hoc/InfluencerChannelDashboard/ChooseAccessType';
import AgencyUsersListPage from './hoc/AgencyUserManagement/AgencyUsersListPage';
import UserAddAndEditPage from './hoc/AgencyUserManagement/UserAddAndEditPage';
import AgencyBrandListPage from './hoc/AgencyBrandManagement/AgencyBrandListPage';
import CreateBrandPage from './hoc/AgencyBrandManagement/CreateBrandPage';
// import AgencyDashboard from './components/main/AgencyDashboard/AgencyDashboard';

// Extract routes into a component that can subscribe to Redux
const AppRoutes = () => {
  const userType = useSelector(state => state.utilReducers?.userType);
  const agencyRoleName = useSelector(state => state.getUserInfoReducer?.data?.agencyRoleName || "");

  const AgencyDashboard = () => <BrandDashboardV2 />; // Can add Agency-specific props if needed
  const AgencyMyCampaigns = () => <MyCampaigns />; // Can add Agency-specific props if needed
  const AgencyCreateCampaign = () => <CreateCampaign />; // Can add Agency-specific props if needed
  const AgencyBrandBusinessDashboard = () => <BrandBusinessDashboard />; // Can add Agency-specific props if needed
  const AgencyWalletAndTransactions = () => <BrandWalletAndTransactions />; // Can add Agency-specific props if needed
  const AgencyBrandCampaignList = () => <BrandCampaignList />; // Can add Agency-specific props if needed
  const AgencyBrandCampaignDetails = () => <BrandCampaignsDetails />; // Can add Agency-specific props if needed

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/registration/brand" element={<Registration loginType="BRAND" loginDailog1='Unleash Brand, Forge Connections.' />} />
      <Route path="/registration/influencer" element={<Registration loginType="INFLUENCER" loginDailog1='Unleash Influencer, Forge Connections.' />} />
      <Route path="/registration/agency" element={<Registration loginType="AGENCY" loginDailog1='Unleash Agency, Forge Connections.' />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:userType" element={<ResetPassword />} />
      <Route path="/verifyOtp/:userType" element={<Verify />} />
      <Route path="/verifyEmailOtp/:userType" element={<VerifyEmailOtp />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Forbidden Route */}
      <Route path="/403" element={<Forbidden />} />

      {/* <Route path="/brand-dashboard-v2" element={<BrandDashboardV2 />} /> */}

      {/* Catch-all Route for 404 */}
      <Route path="*" element={<NotFound />} />
      {/* Wrap protected routes in PrivateRoute */}
      <Route exact path="/" element={userType === "BRAND" ? <Navigate to="/brand" /> : userType === "INFLUENCER" ? <Navigate to="/influencer" /> : userType === "AGENCY" ? <Navigate to="/agency" /> : <Navigate to="/login" />} />
      {/* Brand Routes */}
      <Route path="/brand/*" element={<PrivateRoute allowedType="BRAND"><BrandDashboardV2 /></PrivateRoute>} />
      <Route path="/brand/campaigns" element={<PrivateRoute allowedType="BRAND"><MyCampaigns /></PrivateRoute>} />
      <Route path="/brand/campaigns/create" element={<PrivateRoute allowedType="BRAND"><CreateCampaign /></PrivateRoute>} />
      <Route path="/brand/busisness" element={<PrivateRoute allowedType="BRAND"><BrandBusinessDashboard /></PrivateRoute>} />
      <Route path="/brand/campaigns/campaign-list" element={<PrivateRoute allowedType="BRAND"><BrandCampaignList /></PrivateRoute>} />  {/* CampaignList Page  */}
      <Route path="/brand/transactions" element={<PrivateRoute allowedType="BRAND"><BrandWalletAndTransactions /></PrivateRoute>} />
      <Route path="/brand/campaigns/details" element={<PrivateRoute allowedType="BRAND"><BrandCampaignsDetails /></PrivateRoute>} />
      {/* Campaign ApprovedList Page  */}

      {/* Influencer Routes */}
      <Route path="/influencer/choose-access-type" element={<PrivateRoute allowedType="INFLUENCER"><ChooseAccessType /></PrivateRoute>} />
      <Route path="/influencer/*" element={<PrivateRoute allowedType="INFLUENCER"><InfluencerDashboard /></PrivateRoute>} />
      <Route path="/influencer-channel" element={<PrivateRoute allowedType="INFLUENCER"><InfluencerChannelDashboard /></PrivateRoute>} />
      <Route path="/influencer-channel-auth" element={<PrivateRoute allowedType="INFLUENCER"><InfluencerChannelAuthOnly /></PrivateRoute>} />
      <Route path="/influencer/campaigns" element={<PrivateRoute allowedType="INFLUENCER"><CampaignsList /></PrivateRoute>} />
      <Route path="/influencer/campaigns/details" element={<PrivateRoute allowedType="INFLUENCER"><CampaignsDetails /></PrivateRoute>} />
      <Route path="/influencer/campaigns/unique-detail/:campId" element={<PrivateRoute allowedType="INFLUENCER"><CampaignsDetails /></PrivateRoute>} />
      <Route path="/influencer/campaigns/upload" element={<PrivateRoute allowedType="INFLUENCER"><UploadCampaignVideos /></PrivateRoute>} />
      <Route path="/influencer/campaigns/ugc-upload" element={<PrivateRoute allowedType="INFLUENCER"><UploadCampaignUGCVideos /></PrivateRoute>} />
      <Route path="/influencer/approved-list" element={<PrivateRoute allowedType="INFLUENCER"><ApprovedList /></PrivateRoute>} />
      <Route path="/influencer/transactions" element={<PrivateRoute allowedType="INFLUENCER"><InfluencerWalletAndTransactions /></PrivateRoute>} />
      <Route path="/influencer/my-channels" element={<PrivateRoute allowedType="INFLUENCER"><MyChannelsPage /></PrivateRoute>} />
      <Route path="/influencer/youtube-not-published-videos" element={<PrivateRoute allowedType="INFLUENCER"><YoutubeNotPublishedVideos /></PrivateRoute>} />
      {/* Agency Routes */}
      <Route path="/agency/*" element={<PrivateRoute allowedType="AGENCY"><AgencyDashboard /></PrivateRoute>} />
      <Route path="/agency/campaigns" element={<PrivateRoute allowedType="AGENCY"><AgencyMyCampaigns /></PrivateRoute>} />
      {agencyRoleName === "Admin" && (
        <>
          <Route path="/agency/user-management" element={<PrivateRoute allowedType="AGENCY"><AgencyUsersListPage /></PrivateRoute>} />
          <Route path="/agency/user-management/add-edit" element={<PrivateRoute allowedType="AGENCY"><UserAddAndEditPage /></PrivateRoute>} />
        </>
      )}
      {(agencyRoleName === "Admin" || agencyRoleName === "Manager") && (
        <>
          <Route path="/agency/campaigns/create" element={<PrivateRoute allowedType="AGENCY"><AgencyCreateCampaign /></PrivateRoute>} />
          <Route path="/agency/busisness" element={<PrivateRoute allowedType="AGENCY"><AgencyBrandBusinessDashboard /></PrivateRoute>} />
          <Route path="/agency/brand-management" element={<PrivateRoute allowedType="AGENCY"><AgencyBrandListPage /></PrivateRoute>} />
          <Route path="/agency/brand-management/create-brand" element={<PrivateRoute allowedType="AGENCY"><CreateBrandPage /></PrivateRoute>} />
        </>
      )}
      <Route path="/agency/transactions" element={<PrivateRoute allowedType="AGENCY"><AgencyWalletAndTransactions /></PrivateRoute>} />
      <Route path="/agency/campaigns/campaign-list" element={<PrivateRoute allowedType="AGENCY"><AgencyBrandCampaignList /></PrivateRoute>} />
      <Route path="/agency/campaigns/details" element={<PrivateRoute allowedType="AGENCY"><AgencyBrandCampaignDetails /></PrivateRoute>} />
      {/* <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} /> */}

      {/* <Route path="/dashboard-social-account" element={<PrivateRoute><DashboardSocialAccount /></PrivateRoute>} />
                <Route path="/pay-out-dashboard" element={<PrivateRoute><PayoutDashboard /></PrivateRoute>} />
                <Route path="/dashboard-shipping-address" element={<PrivateRoute><DashboardShippingAddress /></PrivateRoute>} />
                <Route path="/resource-page" element={<ResourcePage />} /> */}
    </Routes>
  );
};

function App() {
  React.useEffect(() => {
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: APPLE_CONFIG.clientId,
        scope: 'name email',
        redirectURI: APPLE_CONFIG.redirectUri,
        state: 'origin_web',
        nonce: 'nonce_12345',
        usePopup: true
      });
      console.log("Apple Sign In initialized globally");
    }
  }, []);

  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <div className="App">
          <GlobalLoader />
          <MergePopup />
          {/* Wrap the application with GoogleOAuthProvider */}
          <GoogleOAuthProvider clientId="400968589113-remh198t8i5o65b5gc1nn5qnc7paha92.apps.googleusercontent.com">
            <ToastProvider defaultPosition="top-center" defaultDuration={5000}>
              <AppRoutes />
            </ToastProvider>
          </GoogleOAuthProvider>
        </div>
      </PersistGate>
    </Provider>
  );
}

export default App;
